import React, { useState } from 'react';
import { Checkbox, Dialog, FormControlLabel } from '@mui/material';
import Nbsp from './Nbsp';
import { Close as CloseIcon } from '@mui/icons-material';
import CheckedXIcon from './CheckedXIcon';
import { REPORT_FORMAT_TYPES } from '@shared/constants/reportFormatTypes';

const DOWNLOAD_ERROR_MESSAGE = 'Please select a report to download.';
const DOWNLOAD_TEXT = 'Which report(s) would you like to download?';
const REPORT_FORMAT_TEXT = {
  [REPORT_FORMAT_TYPES.CSV]: 'CSV',
  [REPORT_FORMAT_TYPES.PDF]: 'PDF'
};

/**
 * @typedef {object} DownloadModalWithButtonReports
 * @property {string} title
 * @property {string|number} [value]
 * @property {boolean} [disabled]
 */

/**
 * @typedef {object} DownloadModalWithButtonProps
 * @property {DownloadModalWithButtonReports[]} [reports]
 * @property {(selectedValues: Array<string | number>, reportFormat: number) => void | Promise<void>} downloadReports
 * @property {Array<number>} [reportFormatOptions]
 * @property {string} [downloadText]
 */

/**
 * @param {DownloadModalWithButtonProps} props
 */
const DownloadModalWithButton = props => {
  const {
    reports = [],
    downloadReports,
    reportFormatOptions = [REPORT_FORMAT_TYPES.CSV],
    downloadText = DOWNLOAD_TEXT
  } = props;

  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const closeModal = () => {
    setDownloadModalOpen(false);
    setSelectedReports([]);
    setErrorMessage(null);
  };

  const handleDownload = async reportFormat => {
    if (!selectedReports.length) {
      return setErrorMessage(DOWNLOAD_ERROR_MESSAGE);
    }

    const selectedValues = selectedReports.map(
      reportName => reports.find(r => r.title === reportName).value
    );
    await downloadReports(selectedValues, reportFormat);

    closeModal();
  };

  const toggleReportSelection = event => {
    setErrorMessage(null);
    const report = event.target.dataset['report'];

    setSelectedReports(prev => {
      if (event.target.checked) {
        return [...prev, report];
      }
      return prev.filter(r => r !== report);
    });
  };

  return (
    <div>
      <button onClick={() => setDownloadModalOpen(true)} className='primary-button'>
        Download Report
      </button>

      <Dialog open={downloadModalOpen} onClose={closeModal} maxWidth='md' fullWidth>
        <div className='download-report-modal'>
          <button className='download-report-modal__close' onClick={closeModal}>
            <CloseIcon />
          </button>
          <div className='download-report-modal__content'>
            <h2>{downloadText}</h2>
            <div className='download-report-modal__available-reports'>
              <div
                className='download-report-modal__available-reports__list-box'
                data-testid='available-reports'>
                {reports.map(report => (
                  <FormControlLabel
                    key={`report-${report.title}`}
                    control={
                      <Checkbox
                        slotProps={{ input: { 'data-report': report.title } }}
                        name={report.title}
                        disabled={report.disabled}
                        color='default'
                        checkedIcon={<CheckedXIcon />}
                      />
                    }
                    label={report.title}
                    onChange={toggleReportSelection}
                  />
                ))}
              </div>
            </div>

            <div className='download-report-modal__button-wrapper'>
              {reportFormatOptions.map(reportType => (
                <button
                  key={reportType}
                  className='download-report-modal__submit primary-button'
                  onClick={() => handleDownload(reportType)}>
                  {`Download${
                    reportFormatOptions.length > 1 ? ` ${REPORT_FORMAT_TEXT[reportType]}` : ''
                  }`}
                </button>
              ))}
              <div className='download-report-modal__error'>{errorMessage || <Nbsp />}</div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DownloadModalWithButton;
